
import {
  defineComponent,
  ref,
  computed,
  onMounted,
  Ref,
} from 'vue';

import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import ProgressSpinner from 'primevue/progressspinner';
import Toolbar from 'primevue/toolbar';
import Breadcrumb from 'primevue/breadcrumb';
import SelectButton from 'primevue/selectbutton';
import Divider from 'primevue/divider';

import SpmButton from '@/components/spm-primevue/SpmButton.vue';

import { useI18n } from 'vue-i18n';

import FileItem from '@/components/file-manager/FileItem.vue';
import FolderItem from '@/components/file-manager/FolderItem.vue';
import Spinner from '@/components/TheSpinner.vue';

import { File, Folder } from '@/types';
import { getSharedGraphicElements, createNewFileInMedia } from '@/composables/configs/configuration';
import { UserState } from '@/composables/User';
import { showToastError, showToastSuccess } from '@/helpers';

export default defineComponent({
  name: 'GraphicElements',

  components: {
    Button,
    InputText,
    ProgressSpinner,
    Toolbar,
    Breadcrumb,
    SelectButton,
    Divider,
    SpmButton,
    FileItem,
    FolderItem,
    Spinner,
  },

  props: {
    extensions: {
      type: String,
      required: false,
      default: '',
    },
  },

  emits: ['on-choose-file', 'on-close-dialog'],

  setup(props, context) {
    const { t } = useI18n();
    const idShop = UserState.activeShop?.id ?? 0;

    const files: Ref<File[]> = ref([]);
    const folders: Ref<Folder[]> = ref([]);
    const currentPath = ref('');
    const pathHistory: Ref<string[]> = ref([]);
    const loading = ref(false);
    const displaySpinner = ref(false);
    const hasMore = ref(true);
    const lastFile = ref('');
    const scrollContainer = ref<HTMLElement | null>(null);
    const searchInput = ref('');
    const searchQuery = ref('');
    const activeSearchQuery = ref('');
    const isSearchMode = ref(false);
    const layout = ref('grid');
    const layoutOptions = ['grid', 'list'];

    const breadcrumbItems = computed(() => {
      if (!currentPath.value) return [];
      const parts = currentPath.value.split('/').filter(Boolean);
      return parts.map((part, index) => ({
        label: part,
        command: () => {
          const newPath = parts.slice(0, index + 1).join('/');
          navigateToPath(newPath);
        },
      }));
    });

    const homeBreadcrumb = {
      icon: 'fa fa-home',
      command: () => navigateToPath(''),
    };

    const navigateToPath = (path: string) => {
      currentPath.value = path;
      pathHistory.value = [];
      hasMore.value = true;
      lastFile.value = '';
      fetchData();
    };

    const fetchData = async (append = false) => {
      if (loading.value || (!append && !hasMore.value)) return;

      loading.value = true;
      try {
        const response = await getSharedGraphicElements(
          currentPath.value,
          append ? lastFile.value : undefined,
          searchQuery.value || undefined,
        );

        if (response?.data) {
          const newFiles: File[] = [];
          const newFolders: Folder[] = [];
          const responseData = response.data.files || response.data;

          Object.values(responseData).forEach((item: any) => {
            if (item.is_dir) {
              newFolders.push({
                id: item.path,
                name: item.name,
                path: item.path,
                previews: item.previews || [],
              });
            } else {
              newFiles.push({
                id: item.path || item.name,
                url: `https://media.shopimind.io/clients/${item.src}`,
                type: item.type,
              });
            }
          });

          if (append) {
            files.value = [...files.value, ...newFiles];
          } else {
            folders.value = newFolders;
            files.value = newFiles;
          }

          hasMore.value = response.data.moreFiles ?? false;
          lastFile.value = response.data.lastFile ?? '';
        }
      } catch (error) {
        console.error('Error fetching shared elements:', error);
      } finally {
        loading.value = false;
      }
    };

    const openFolder = (folder: Folder) => {
      pathHistory.value.push(currentPath.value);
      currentPath.value = folder.path;
      hasMore.value = true;
      lastFile.value = '';
      searchQuery.value = '';
      searchInput.value = '';
      fetchData();
    };

    const goBack = () => {
      if (pathHistory.value.length > 0) {
        currentPath.value = pathHistory.value.pop() || '';
        hasMore.value = true;
        lastFile.value = '';
        searchQuery.value = '';
        searchInput.value = '';
        fetchData();
      }
    };

    const handleSearch = () => {
      if (searchQuery.value.trim()) {
        activeSearchQuery.value = searchQuery.value.trim();
        isSearchMode.value = true;
        hasMore.value = true;
        lastFile.value = '';
        fetchData();
      }
    };

    const clearSearch = () => {
      searchQuery.value = '';
      activeSearchQuery.value = '';
      isSearchMode.value = false;
      hasMore.value = true;
      lastFile.value = '';
      fetchData();
    };

    const handleReturn = () => {
      goBack();
    };

    const handleScroll = async () => {
      if (!scrollContainer.value || loading.value || !hasMore.value) return;

      const { scrollTop, scrollHeight, clientHeight } = scrollContainer.value;
      if (scrollTop + clientHeight >= scrollHeight - 100) {
        await fetchData(true);
      }
    };

    const getBase64FromUrl = async (url: string): Promise<string> => {
      const response = await fetch(url);
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    };

    const handleChooseFile = async (params: any) => {
      if (params.action === 'download') {
        displaySpinner.value = true;
        try {
          const base64 = await getBase64FromUrl(params.file.url);
          const uploadResult = await createNewFileInMedia([base64], idShop, '');
          if (uploadResult.data && uploadResult.data.length > 0) {
            const newUrl = `https://media.shopimind.io/clients/${uploadResult.data[0].path}`;
            context.emit('on-choose-file', { file: { ...params.file, url: newUrl }, action: 'download' });
            showToastSuccess(t('fileManager.imageDownloaded'));
            context.emit('on-close-dialog');
          }
        } catch (error) {
          await showToastError(t('errorMessages.GENERIC_ERROR'));
        } finally {
          displaySpinner.value = false;
        }
      }
    };

    onMounted(() => {
      fetchData();
    });

    return {
      t,
      files,
      folders,
      currentPath,
      loading,
      displaySpinner,
      scrollContainer,
      searchQuery,
      activeSearchQuery,
      isSearchMode,
      layout,
      layoutOptions,
      breadcrumbItems,
      homeBreadcrumb,

      openFolder,
      goBack,
      handleSearch,
      clearSearch,
      handleReturn,
      handleScroll,
      handleChooseFile,
    };
  },
});
